import clsx from 'clsx';
import { Loader } from '@/components/Loader';
import { phaseService } from '@/services/phaseApi';
import {
  TabsContent,
  TabsList,
  Root as TabsRoot,
  TabsTrigger,
} from '@radix-ui/react-tabs';
import { useEffect, useMemo, useState } from 'react';
import { useAccount as useEVMAccount } from 'wagmi';
import { ActiveAdventures } from './components/ActiveAdventures';
import { MyAdventures } from './components/MyAdventures';
import { AdventuresTabs } from './types';
import { useQuestsAdventuresStore } from './store';
import { Adventure } from '@/types/phases';
import './styles.css';

export default function QuestsAdventure() {
  const { address } = useEVMAccount();
  const [isLoading, setIsLoading] = useState(true);
  const [tab, setTab] = useState(AdventuresTabs.ActiveAdventures);
  const [adventures, tasks] = useQuestsAdventuresStore(s => [
    s.adventures,
    s.tasks,
  ]);

  useEffect(() => {
    if (address) {
      setIsLoading(true);
      phaseService
        .getAdventuresWithTasks(address)
        .finally(() => setIsLoading(false));
    }
  }, [address]);

  const [activeAdventures, completedAdventures] = useMemo(() => {
    const active: Adventure[] = [];
    const completed: Adventure[] = [];

    adventures.forEach(adventure => {
      const adventureTasks = tasks[adventure.id] || []; // Get tasks for the adventure, or an empty array if none exist
      const allTasksCompleted = adventureTasks.every(task => task.isCompleted);

      if (allTasksCompleted) completed.push(adventure);
      else active.push(adventure);
    });

    return [active, completed];
  }, [adventures, tasks]);

  if (isLoading) {
    return (
      <div className="position-relative d-flex justify-content-center align-items-center h-100">
        <Loader />
      </div>
    );
  }

  return (
    <TabsRoot value={tab} onValueChange={val => setTab(val as AdventuresTabs)}>
      <TabsList
        className={clsx(
          'quests-adventures--tabs',
          `_${Object.values(AdventuresTabs).findIndex(el => el === tab)}`
        )}
      >
        <TabsTrigger
          className="quests-adventures--tabs-item"
          value={AdventuresTabs.ActiveAdventures}
        >
          <span>Active</span>
        </TabsTrigger>
        <TabsTrigger
          className="quests-adventures--tabs-item"
          value={AdventuresTabs.MyAdventures}
        >
          <span>My Adventures</span>
        </TabsTrigger>
      </TabsList>
      <TabsContent value={AdventuresTabs.ActiveAdventures}>
        <ActiveAdventures adventures={activeAdventures} />
      </TabsContent>
      <TabsContent value={AdventuresTabs.MyAdventures}>
        <MyAdventures adventures={completedAdventures} />
      </TabsContent>
    </TabsRoot>
  );
}
